// PubblicitaPrima.tsx — v045: per chi è in Base, prima di una partita una pagina con un annuncio (al massimo ogni
// `app.pubblicita_prima_ogni_minuti`, predefinito 10). Dopo `app.pubblicita_prima_secondi` (5) compare «Gioca».
// Se l'annuncio non si carica (AdSense spento o bloccato) la pagina non si vede nemmeno: si va dritti alla partita.
// v046: nelle app Android/iOS al posto della pagina c'è l'annuncio a tutto schermo di AdMob (servizi/admob.ts): alla
// sua chiusura si va avanti; se non si carica si va avanti subito, come sul web.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Annuncio, slotAnnuncio } from "./AnnuncioRisultato.tsx";
import { pubblicitaAttiva } from "../servizi/pubblicita.ts";
import { statoAccount } from "../servizi/account.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { IMP_APP, PREFERENZE } from "../costanti.ts";
import { NATIVA } from "../servizi/nativo.ts";
import { admobDisponibile, interstizialeAdMob } from "../servizi/admob.ts";

/** Va mostrata adesso? (Base, pubblicità accesa, slot impostato, ultima volta abbastanza tempo fa) */
export function servePubblicitaPrima(): boolean {
  const s = statoAccount();
  if (s.tipo === "registrato" && s.io.piano !== "base") return false;
  if (!pubblicitaAttiva() || !(NATIVA ? admobDisponibile("interstiziale") : slotAnnuncio("prima"))) return false;
  const ogni = leggiImpostazione(IMP_APP.pubblicitaPrimaOgniMinuti, 10, eNumero);
  if (ogni < 0) return false;
  const ultima = Number(leggiPreferenza(PREFERENZE.pubblicitaPrima) ?? "0");
  return !Number.isFinite(ultima) || Date.now() - ultima >= ogni * 60_000;
}

export function PubblicitaPrima({ onFine }: { onFine: () => void }) {
  return NATIVA ? <PrimaAdMob onFine={onFine} /> : <PrimaWeb onFine={onFine} />;
}

/** App: l'annuncio a tutto schermo; la pagina sotto non si vede (finisce quando l'annuncio si chiude o manca). */
function PrimaAdMob({ onFine }: { onFine: () => void }) {
  useEffect(() => {
    let chiuso = false;
    void interstizialeAdMob()
      .then((visto) => {
        if (visto) salvaPreferenza(PREFERENZE.pubblicitaPrima, String(Date.now()));
      })
      .catch(() => undefined)
      .finally(() => !chiuso && onFine());
    return () => {
      chiuso = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function PrimaWeb({ onFine }: { onFine: () => void }) {
  const { t } = useTranslation();
  const secondi = Math.max(0, Math.round(leggiImpostazione(IMP_APP.pubblicitaPrimaSecondi, 5, eNumero)));
  const [pronto, setPronto] = useState<boolean | null>(null);
  const [resta, setResta] = useState(secondi);
  useEffect(() => {
    if (pronto === false) onFine();
    if (pronto !== true) return;
    salvaPreferenza(PREFERENZE.pubblicitaPrima, String(Date.now()));
    const id = setInterval(() => setResta((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronto]);
  return createPortal(
    <div className="ld-pubblicita-prima" role="dialog" aria-modal="true" aria-label={t("pubblicita.etichetta")} data-pronto={pronto === true || undefined}>
      <div className="ld-pubblicita-prima-carta">
        <p className="ld-pubblicita-prima-titolo">{t("pubblicita.prima_titolo")}</p>
        <Annuncio posto="prima" onPronto={setPronto} />
        <button type="button" className="ld-cta ld-cta-centrata" disabled={resta > 0} onClick={onFine}>
          {resta > 0 ? t("pubblicita.prima_attendi", { n: resta }) : t("pubblicita.prima_gioca")}
        </button>
        <p className="nota-centrata">{t("pubblicita.prima_nota")}</p>
      </div>
    </div>,
    document.body,
  );
}
